import { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValue,
  useMotionValueEvent,
  animate,
} from 'framer-motion';
import useGsapHero from '../../hooks/useGsapHero';
import useGsapInView from '../../hooks/useGsapInView';
import ContactTrigger from '../../components/ContactTrigger';
import { resetScrollForRouteChange } from '../../utils/scrollToTop';
import useCountUp from '../../hooks/useCountUp';
import { ClipRevealText } from '../../components/TextEffects';
import {
  allWorks,
  WORK_STATS,
  WORK_FILTER_OPTIONS,
  WORK_FILTER_COUNTS,
} from '../../data/work';
import './WorkPage.css';

const EASE = [0.16, 1, 0.3, 1];
const TRACK1 = allWorks.slice(0, 6);
const TRACK2 = allWorks.slice(6);
const DEFAULT_METRICS = { cardW: 300, gap: 20 };

const TIMING = {
  flatten: 0.3,
  flipHalf: 0.25,
  gridSettle: 0.4,
};

const readCardMetrics = () => {
  if (typeof window === 'undefined') return DEFAULT_METRICS;
  const root = document.querySelector('.work-page');
  if (!root) return DEFAULT_METRICS;
  const styles = getComputedStyle(root);
  return {
    cardW: parseFloat(styles.getPropertyValue('--work-card-w')) || DEFAULT_METRICS.cardW,
    gap: parseFloat(styles.getPropertyValue('--work-card-gap')) || DEFAULT_METRICS.gap,
  };
};

const getTrackScrollRange = (cardW, gap, track1W, track2W) => {
  const viewW = window.innerWidth;
  const safePad = Math.max(48, Math.min(88, viewW * 0.065));
  const visibleW = viewW - safePad * 2;
  const edgeInset = 16;
  return {
    start: edgeInset,
    end: visibleW - track1W - edgeInset,
    track2Start: edgeInset + 72,
    track2End: visibleW - track2W - edgeInset,
  };
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const WorkMetaStat = ({ label, value, sublabel }) => (
  <div className="work-meta-col">
    <span className="work-meta-label">{label}</span>
    <span className="work-meta-value tabular">{value}</span>
    {sublabel && <span className="work-meta-sublabel">{sublabel}</span>}
  </div>
);

const WorkCard = ({ card, layoutMode, activeFilter, flipEdge, onNavigate }) => {
  const isMatch = activeFilter === 'all' || card.category === activeFilter;
  const rotateY =
    flipEdge || (layoutMode === 'grid' && activeFilter !== 'all' && !isMatch) ? 90 : 0;
  const isHidden = layoutMode === 'grid' && activeFilter !== 'all' && !isMatch;

  return (
    <motion.article
      className={`work-card${isHidden ? ' is-filter-hidden' : ''}`}
      data-id={card.index}
      data-category={card.category}
      data-testid={`work-card-${card.index}`}
      animate={{ rotateY }}
      whileHover={
        layoutMode === 'marquee' && !isHidden && !flipEdge
          ? { scale: 1.04, y: -4, transition: { duration: 0.25 } }
          : undefined
      }
      transition={{ duration: TIMING.flipHalf, ease: EASE }}
      style={{ transformStyle: 'preserve-3d' }}
      onClick={() => onNavigate(card.path)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigate(card.path);
        }
      }}
      role="link"
      tabIndex={isHidden ? -1 : 0}
      aria-hidden={isHidden}
    >
      <img src={card.image} alt="" className="work-card-img" loading="lazy" decoding="async" />
      <div className="work-card-gradient" aria-hidden="true" />
      <span className="work-card-num tabular">{card.index}</span>
      <h2 className="work-card-title">{card.title}</h2>
      <p className="work-card-meta">{card.meta}</p>
    </motion.article>
  );
};

const WorkMarquee = ({ activeFilter, onFilterChange }) => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [metrics, setMetrics] = useState(DEFAULT_METRICS);
  const [layoutMode, setLayoutMode] = useState('marquee');
  const [scrollLocked, setScrollLocked] = useState(false);
  const [flipEdge, setFlipEdge] = useState(false);
  const [rotateDeg, setRotateDeg] = useState(-4);
  const [isAnimating, setIsAnimating] = useState(false);
  const animatingRef = useRef(false);

  const defaultTrack1W =
    TRACK1.length * DEFAULT_METRICS.cardW + (TRACK1.length - 1) * DEFAULT_METRICS.gap;
  const defaultTrack2W =
    TRACK2.length * DEFAULT_METRICS.cardW + (TRACK2.length - 1) * DEFAULT_METRICS.gap;

  const [scrollRange, setScrollRange] = useState(() =>
    typeof window !== 'undefined'
      ? getTrackScrollRange(
          DEFAULT_METRICS.cardW,
          DEFAULT_METRICS.gap,
          defaultTrack1W,
          defaultTrack2W
        )
      : { start: 20, end: -1200, track2Start: 92, track2End: -1200 }
  );

  const { cardW, gap } = metrics;
  const track1W = TRACK1.length * cardW + (TRACK1.length - 1) * gap;
  const track2W = TRACK2.length * cardW + (TRACK2.length - 1) * gap;
  const track2Offset = (track1W - track2W) / 2;

  const scrollActive = layoutMode === 'marquee' && !scrollLocked;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  const scrollX1 = useTransform(scrollYProgress, [0, 1], [scrollRange.start, scrollRange.end]);
  const scrollX2 = useTransform(
    scrollYProgress,
    [0, 1],
    [scrollRange.track2Start, scrollRange.track2End]
  );

  const track1X = useMotionValue(scrollRange.start);
  const track2X = useMotionValue(scrollRange.track2Start);

  useMotionValueEvent(scrollX1, 'change', (v) => {
    if (scrollActive) track1X.set(v);
  });

  useMotionValueEvent(scrollX2, 'change', (v) => {
    if (scrollActive) track2X.set(v);
  });

  useEffect(() => {
    if (scrollActive) {
      track1X.set(scrollX1.get());
      track2X.set(scrollX2.get());
    }
  }, [scrollActive, scrollRange, track1X, track2X, scrollX1, scrollX2]);

  useEffect(() => {
    const update = () => {
      const next = readCardMetrics();
      const t1 = TRACK1.length * next.cardW + (TRACK1.length - 1) * next.gap;
      const t2 = TRACK2.length * next.cardW + (TRACK2.length - 1) * next.gap;
      setMetrics(next);
      setScrollRange(getTrackScrollRange(next.cardW, next.gap, t1, t2));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const switchGridFilter = useCallback(
    async (filterId) => {
      if (animatingRef.current) return;
      animatingRef.current = true;
      setIsAnimating(true);

      setFlipEdge(true);
      await wait(TIMING.flipHalf * 1000);

      onFilterChange(filterId);
      setFlipEdge(false);

      await wait(TIMING.flipHalf * 1000);
      animatingRef.current = false;
      setIsAnimating(false);
    },
    [onFilterChange]
  );

  const runToGrid = useCallback(
    async (filterId) => {
      if (animatingRef.current) return;
      animatingRef.current = true;
      setIsAnimating(true);
      setScrollLocked(true);

      setRotateDeg(0);
      await Promise.all([
        animate(track1X, 0, { duration: TIMING.flatten, ease: EASE }),
        animate(track2X, 0, { duration: TIMING.flatten, ease: EASE }),
      ]);

      setFlipEdge(true);
      await wait(TIMING.flipHalf * 1000);

      onFilterChange(filterId);
      setLayoutMode('grid');
      setFlipEdge(false);

      await wait(TIMING.gridSettle * 1000);
      setScrollLocked(false);
      animatingRef.current = false;
      setIsAnimating(false);
    },
    [onFilterChange, track1X, track2X]
  );

  const runToMarquee = useCallback(async () => {
    if (animatingRef.current) return;
    animatingRef.current = true;
    setIsAnimating(true);
    setScrollLocked(true);

    setFlipEdge(true);
    await wait(TIMING.flipHalf * 1000);

    setLayoutMode('marquee');
    onFilterChange('all');

    await wait(50);
    setFlipEdge(false);
    await wait(TIMING.flipHalf * 1000);

    setRotateDeg(-4);
    track1X.set(scrollX1.get());
    track2X.set(scrollX2.get());
    setScrollLocked(false);

    await wait(100);
    animatingRef.current = false;
    setIsAnimating(false);
  }, [onFilterChange, scrollX1, scrollX2, track1X, track2X]);

  const handleFilterClick = useCallback(
    (filterId) => {
      if (filterId === activeFilter || isAnimating) return;
      if (filterId === 'all') {
        runToMarquee();
      } else if (layoutMode === 'grid') {
        switchGridFilter(filterId);
      } else {
        runToGrid(filterId);
      }
    },
    [activeFilter, isAnimating, layoutMode, runToGrid, runToMarquee, switchGridFilter]
  );

  const handleProjectNavigate = useCallback(
    (path) => {
      resetScrollForRouteChange();
      navigate(path);
    },
    [navigate],
  );

  const cardProps = { layoutMode, activeFilter, flipEdge, onNavigate: handleProjectNavigate };

  return (
    <>
      <nav className="work-filters" aria-label="Filter projects by category">
        {WORK_FILTER_OPTIONS.map((opt) => {
          const isActive = activeFilter === opt.id;
          const showCount = isActive && opt.id !== 'all';
          return (
            <button
              key={opt.id}
              type="button"
              className={`work-filter-btn${isActive ? ' is-active' : ''}`}
              onClick={() => handleFilterClick(opt.id)}
              disabled={isAnimating}
              aria-pressed={isActive}
              data-testid={`work-filter-${opt.id}`}
            >
              <span className="work-filter-label">{opt.label}</span>
              {opt.id !== 'all' && (
                <span className="work-filter-count-slot" aria-hidden={!showCount}>
                  <span className="work-filter-count-ghost">
                    {' '}
                    ({String(WORK_FILTER_COUNTS[opt.id]).padStart(2, '0')})
                  </span>
                  <AnimatePresence>
                    {showCount && (
                      <motion.span
                        key={`${opt.id}-count`}
                        className="work-filter-count"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, ease: EASE }}
                      >
                        {' '}
                        ({String(WORK_FILTER_COUNTS[opt.id]).padStart(2, '0')})
                      </motion.span>
                    )}
                  </AnimatePresence>
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <section
        className={`work-marquee-section${layoutMode === 'grid' ? ' is-grid-mode' : ' is-marquee-mode'}${
          scrollLocked ? ' is-scroll-locked' : ''
        }`}
        ref={sectionRef}
        data-testid="work-marquee"
        aria-label="All projects"
      >
        <div className="work-marquee-pin">
          <div className="work-marquee-stage">
            <div className="work-marquee-safe">
              <motion.div
                className={`work-cards-layout${layoutMode === 'grid' ? ' is-grid' : ' is-marquee'}`}
                animate={{ rotate: rotateDeg }}
                transition={{ duration: TIMING.flatten, ease: EASE }}
                style={{ '--track2-offset': `${track2Offset}px` }}
              >
                <motion.div
                  className="work-marquee-track work-marquee-track--1"
                  style={{ x: layoutMode === 'grid' ? 0 : track1X }}
                >
                  {TRACK1.map((card) => (
                    <WorkCard key={card.index} card={card} {...cardProps} />
                  ))}
                </motion.div>
                <motion.div
                  className="work-marquee-track work-marquee-track--2"
                  style={{ x: layoutMode === 'grid' ? 0 : track2X }}
                >
                  {TRACK2.map((card) => (
                    <WorkCard key={card.index} card={card} {...cardProps} />
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const WorkPage = () => {
  const headerRef = useRef(null);
  const metaRef = useRef(null);
  const headerInView = useGsapHero(headerRef);
  const metaInView = useGsapInView(metaRef);
  const [activeFilter, setActiveFilter] = useState('all');

  const totalCount = useCountUp(WORK_STATS.total, {
    duration: 1000,
    active: metaInView,
    once: true,
  });
  const disciplinesCount = useCountUp(WORK_STATS.disciplines, {
    duration: 1000,
    active: metaInView,
    once: true,
  });

  return (
    <main className="work-page" data-testid="work-page">
      <header ref={headerRef} className="work-header">
        <div className="work-header-top gsap-reveal">
          <span className="work-header-strip">(002) —— WORK · FULL INDEX</span>
          <span className="work-header-entries tabular">
            {String(WORK_STATS.total).padStart(2, '0')} ENTRIES
          </span>
        </div>

        <div className="work-title-wrap">
          <h1 className="work-title">
            <ClipRevealText text="Work." as="span" externalInView={headerInView} delay={0} duration={0.8} />
            <span className="work-title-arrow gsap-reveal" aria-hidden="true">
              ↗
            </span>
          </h1>
          <div className="work-title-line gsap-hero-line" aria-hidden="true" />
        </div>

        <div className="work-meta gsap-reveal" ref={metaRef}>
          <WorkMetaStat
            label="— PROJECTS"
            value={String(totalCount).padStart(2, '0')}
            sublabel="ARCHIVED"
          />
          <WorkMetaStat
            label="— DISCIPLINES"
            value={String(disciplinesCount).padStart(2, '0')}
            sublabel="ACROSS"
          />
          <WorkMetaStat label="— SINCE" value={WORK_STATS.since} />
        </div>
      </header>

      <WorkMarquee activeFilter={activeFilter} onFilterChange={setActiveFilter} />

      <footer className="work-footer">
        <div className="work-footer-top">
          <div className="work-footer-additional">
            <p className="work-footer-additional-line1">
              — ADDITIONAL WORK AVAILABLE ON REQUEST
            </p>
            <p className="work-footer-additional-line2">
              Landing pages · Custom builds · Brand collateral
            </p>
          </div>
          <nav className="work-footer-nav" aria-label="Footer navigation">
            <Link to="/" className="work-footer-link">
              ← Back to homepage
            </Link>
            <ContactTrigger className="work-footer-link">
              Contact ↗
            </ContactTrigger>
          </nav>
        </div>
        <p className="work-footer-end">
          — END OF SELECTION —
        </p>
      </footer>
    </main>
  );
};

export default WorkPage;
